// global-search.js — Global search and replace panel.
//
// Provides find-across-all-notes functionality with live search,
// case-sensitive toggle, single/all replace, and keyboard navigation.

import {
  CALENDARS_NOTE,
  GRAPH_NOTE,
  getCurrentFileName,
  mobileMediaQuery,
  reflectActiveView,
} from '../core/app-state.js';
import { NoteEditor } from '../editor/note-editor.js';
import { activateSearchSurface, deactivateSearchSurface } from '../ui/panels.js';
import { createKeyedList } from '../ui/keyed-list.js';
import type { Cell, KeyedList } from '../ui/keyed-list.js';
import { confirmStatus } from '../ui/status-pill.js';
import { escapeRegExp } from '../utils/escape-regexp.js';
import { createSearchPredicate } from '../utils/search-predicate.js';
import { debounce } from '../utils/debounce.js';
import { recordLocalEcho } from '../storage/note-local-echo.js';
import { openLocation } from './deep-link.js';
import { updateFileList } from './file-list.js';
import { handleRenameAfterReplace } from './note-manager.js';
import { findAllBareTerms, replaceBareHits } from './note-mention.js';

// The panel's elements are typed `any` — the inputs/checkbox/results are read
// for .value/.checked/.select/.offsetLeft which the bare HTMLElement lacks.
const globalSearchPanel = document.getElementById('global-search-panel') as any;
const gsSearchInput = document.getElementById('gs-search-input') as any;
const gsReplaceInput = document.getElementById('gs-replace-input') as any;
const gsCaseCheckbox = document.getElementById('gs-case-checkbox') as any;
const gsCaseLabel = document.getElementById('gs-case-label') as any;
// "Rail" controls: case / scope / title / replace are all .panel-chip filter
// pills; the Replace chip discloses the replace block.
const gsScopeToggle = document.getElementById('gs-scope-toggle') as any;
const gsTitleToggle = document.getElementById('gs-title-toggle') as any;
const gsReplaceToggle = document.getElementById('gs-replace-toggle') as any;
const gsReplaceBlock = document.getElementById('gs-replace-block') as any;
const gsReplaceBtn = document.getElementById('gs-replace-btn') as any;
const gsReplaceAllBtn = document.getElementById('gs-replace-all-btn') as any;
const gsCloseBtn = document.getElementById('gs-close') as any;
const gsStatus = document.getElementById('gs-status') as any;
const gsResults = document.getElementById('gs-results') as any;

let gsCurrentResults: any[] = [];
let gsSelectedIndex = -1;
// "Bare" mode (set by a programmatic alias open): match only whole-word, body-only
// occurrences — skipping the H1 title, code, and existing links — so wrapping a
// term as a link via Replace-all can't corrupt markup or re-link an existing link.
// Reset to substring search the moment the user types their own query.
//
// The panel's active behaviour:
//   'manual'    substring / boolean replace (the default)
//   'bare'      whole-word body-only alias linking (note-mention)
//   'backlinks' inbound-link references to a note (findNoteReferences); a blank
//               Replace is read-only "find references", a target repoints them
//               (repointLinks). Opened by openBacklinks / Cmd-Shift-B.
// Any manual keystroke resets this to 'manual' (see the input listener).
type GsMode = 'manual' | 'bare' | 'backlinks';
let gsMode: GsMode = 'manual';
const isReferenceMode = () => gsMode === 'backlinks';

// Optional single-note scope (set by the Labyrinth thread alias consolidation). While set,
// find AND replace-all only ever touch this one note — so wrapping a thread term as a link
// can't reach into the wrong note. Combined with bare mode it also skips existing links/code.
// Cleared when the panel closes or the user clicks "search all notes".
let gsScopeNote: string | null = null;
// STICKY scope (#6, Cmd-F "find in this note"): when true the note-scope SURVIVES
// manual typing (the alias-consolidation scope, which never sets this, still
// auto-clears on the first keystroke). The scope chip toggles it; reset on close.
let gsScopeSticky = false;

// Title-scope toggle (the "T" chip): whether the note's H1 title participates in
// search. The chip states the current scope; cycles whole-note → content → title.
type TitleScope = 'both' | 'content' | 'title';
let gsTitleScope: TitleScope = 'both';
// One row per scope: the next scope in the toggle cycle, its full label, and its
// tooltip — all three are always read together for the same scope.
const TITLE_STATES: Record<
  TitleScope,
  { next: TitleScope; label: string; short: string; tip: string }
> = {
  both: { next: 'content', label: 'Whole note', short: 'Whole ▾', tip: 'Search title and content' },
  content: { next: 'title', label: 'Content only', short: 'Content ▾', tip: 'Search content only' },
  title: { next: 'both', label: 'Title only', short: 'Title ▾', tip: 'Search title only' },
};
// Bounded concurrency: replace-all on a slow network was N round trips
// back-to-back; chunking gets ~5x wall-time reduction on a sync-heavy setup
// without risking storage-layer contention. Runs `fn` over `items` in
// fixed-size batches, awaiting each batch before starting the next. Updates
// touching the currently-open note still run on the main thread (they touch DOM).
const REPLACE_CONCURRENCY = 8;
async function mapChunked<T>(items: T[], fn: (item: T) => Promise<void>): Promise<void> {
  for (let i = 0; i < items.length; i += REPLACE_CONCURRENCY) {
    await Promise.all(items.slice(i, i + REPLACE_CONCURRENCY).map(fn));
  }
}

// Title-scope chip: a cycling pill (Whole ▾ → Content ▾ → Title ▾). Its label
// states the current scope; `.active` (a filter is narrowed) rides the chip's
// accent tint; `data-scope` records the state for the click cycle.
function applyTitleScope() {
  if (!gsTitleToggle) return;
  const st = TITLE_STATES[gsTitleScope];
  gsTitleToggle.dataset.scope = gsTitleScope;
  gsTitleToggle.title = st.tip;
  gsTitleToggle.setAttribute('aria-label', st.tip);
  gsTitleToggle.textContent = st.short;
  gsTitleToggle.classList.toggle('active', gsTitleScope !== 'both');
}

// Scope chip: a single toggle (All notes ⇄ This note). `.active` marks the
// non-default (scoped to this note); the label + tooltip state the mode.
function applyScopeLabel() {
  if (!gsScopeToggle) return;
  const scoped = gsScopeSticky && !!gsScopeNote;
  gsScopeToggle.dataset.scope = scoped ? 'note' : 'all';
  gsScopeToggle.textContent = scoped ? 'This note' : 'All notes';
  const tip = scoped ? 'Searching this note (click for all notes)' : 'Searching all notes';
  gsScopeToggle.title = tip;
  gsScopeToggle.setAttribute('aria-label', tip);
  gsScopeToggle.setAttribute('aria-pressed', String(scoped));
  gsScopeToggle.classList.toggle('active', scoped);
}

// Case chip: the "Aa" pill; `.active` (case-sensitive) rides the accent tint,
// mirroring the hidden checkbox. The words live in the tooltip/aria.
function applyCaseLabel() {
  if (!gsCaseLabel) return;
  const on = !!gsCaseCheckbox?.checked;
  const tip = on ? 'Matching case (case-sensitive)' : 'Ignoring case (click to match case)';
  gsCaseLabel.title = tip;
  gsCaseLabel.setAttribute('aria-label', tip);
  gsCaseLabel.classList.toggle('active', on);
}

// Show/hide the replace block (disclosed by the Replace chip). Keeping the chip
// a persistent toggle means it also collapses the block again.
function discloseReplace(show: boolean) {
  if (gsReplaceToggle) {
    gsReplaceToggle.classList.toggle('active', show);
    gsReplaceToggle.setAttribute('aria-expanded', String(show));
  }
  if (gsReplaceBlock) gsReplaceBlock.hidden = !show;
}

/** Byte index where the H1 title line ends (exclusive) — 0 when the first line
 *  isn't a heading, so there's no title region. */
function titleEnd(content: string): number {
  if (!/^#{1,6}\s/.test(content)) return 0;
  const nl = content.indexOf('\n');
  return nl === -1 ? content.length : nl;
}

/** Does a match at `idx` fall within the active title scope? */
function matchInScope(idx: number, content: string): boolean {
  if (gsTitleScope === 'both') return true;
  const te = titleEnd(content);
  return gsTitleScope === 'title' ? idx < te : idx >= te;
}

interface OpenOpts {
  find?: string;
  replace?: string;
  /** undefined ⇒ the implicit 'manual' default. */
  mode?: 'bare' | 'backlinks';
  /** restrict find + replace to this ONE note (the alias-consolidation scope). */
  scopeNote?: string;
  /** keep `scopeNote` active across manual typing (Cmd-F "find in this note"). */
  persistScope?: boolean;
  run?: boolean;
}

/** Open the Find & Replace panel, optionally pre-filled and run. With no opts it
 *  behaves exactly as before (manual substring search). */
function openGlobalSearch(opts: OpenOpts = {}) {
  gsMode = opts.mode ?? 'manual';
  gsScopeNote = opts.scopeNote ?? null;
  gsScopeSticky = !!opts.persistScope;
  applyScopeLabel();
  if (gsMode === 'bare' && gsTitleScope === 'title') {
    // Alias propagation is body-only — don't let a leftover "Title only" scope hide
    // every match (bare matching already excludes the title).
    gsTitleScope = 'both';
    applyTitleScope();
  }
  if (opts.find !== undefined) gsSearchInput.value = opts.find;
  if (opts.replace !== undefined) gsReplaceInput.value = opts.replace;
  if (!mobileMediaQuery.matches) {
    // Desktop: Find & Replace is a docked panel surface in the cycle.
    activateSearchSurface();
  } else {
    // Mobile: the body-level bottom sheet.
    globalSearchPanel.classList.remove('gs-hidden');
  }
  // visibility:hidden makes the input unfocusable, so we must wait for the
  // class removal to commit before calling .focus().  Forcing a synchronous
  // layout flush gets us most of the way there, but Safari occasionally
  // still drops the focus request — re-issue it on the next animation
  // frame and again as a final macrotask fallback.
  void globalSearchPanel.offsetHeight;
  gsSearchInput.focus({ preventScroll: true });
  gsSearchInput.select();
  requestAnimationFrame(() => {
    if (document.activeElement !== gsSearchInput) {
      gsSearchInput.focus({ preventScroll: true });
      gsSearchInput.select();
    }
  });
  setTimeout(() => {
    if (document.activeElement !== gsSearchInput) {
      gsSearchInput.focus({ preventScroll: true });
      gsSearchInput.select();
    }
  }, 50);
  if (opts.run && opts.find) void gsRunFind();
  // Reset the replace disclosure each open: reveal it only when the caller
  // prefilled a replacement (alias / backlinks repoint), else stay find-only.
  discloseReplace(opts.replace !== undefined);
}

/** @returns {void} */
function closeGlobalSearch() {
  gsScopeNote = null; // a scoped alias consolidation doesn't outlive the panel
  gsScopeSticky = false; // reset so a later alias open doesn't inherit a stale sticky scope
  if (!mobileMediaQuery.matches) {
    deactivateSearchSurface();
  } else {
    globalSearchPanel.classList.add('gs-hidden');
  }
}

/**
 * @param {string} query
 * @param {boolean} [caseSensitive]
 * @returns {Promise<any[]>} the matches (current note first).
 */
// Bare/whole-word matches across the vault, using the SAME body-only, code/link/
// title-masking rule as the Labyrinth alias review (note-mention). Built into the
// standard result shape so rendering / selection / replace are unchanged.
/** Build one result row, clamping the snippet to the match's own line so adjacent
 *  lines never bleed in. `source` is what the offsets index into (the masked body in
 *  bare mode, the raw content otherwise); `matchIndex` is the offset into the FULL
 *  note content (it differs from `idx` in bare mode by the masked title length). */
function buildMatch(
  noteName: string,
  source: string,
  idx: number,
  matchLen: number,
  matchText: string,
  matchIndex: number
) {
  // Keep the matched word near the START of the snippet — a short lead-in, then the rest of
  // the sentence after it — so the highlighted word is always visible in the (right-truncated)
  // row instead of being clipped off when it sits deep in a long line.
  const PRE_CONTEXT = 16;
  const POST_CONTEXT = 80;
  const lineStart = source.lastIndexOf('\n', idx - 1) + 1;
  const nl = source.indexOf('\n', idx);
  const lineEnd = nl === -1 ? source.length : nl;
  const start = Math.max(lineStart, idx - PRE_CONTEXT);
  const end = Math.min(lineEnd, idx + matchLen + POST_CONTEXT);
  return {
    noteName,
    matchIndex,
    prefix: (start > lineStart ? '…' : '') + source.slice(start, idx),
    match: matchText,
    suffix: source.slice(idx + matchLen, end) + (end < lineEnd ? '…' : ''),
  };
}

// The searchable real notes: drop hidden/app-managed (dot-prefixed) + the virtual nav notes
// (Note Graph / Settings) — the same set autocomplete's VIRTUAL excludes and the file list
// hides. When a single-note alias scope is active, restrict to JUST that note, so both find
// and the re-scanning replace-all paths only ever change it.
async function gsVaultNotes(): Promise<any[]> {
  // A single-note scope needs only that note — fetch it directly instead of
  // loading (and filtering) the whole vault.
  if (gsScopeNote) {
    const content = await window.NoteStorage.getNote(gsScopeNote);
    return content === null ? [] : [{ name: gsScopeNote, content }];
  }
  return (await window.NoteStorage.getAllNotes()).filter(
    (n: any) => !n.name.startsWith('.') && n.name !== GRAPH_NOTE && n.name !== CALENDARS_NOTE
  );
}

async function gsGetBareMatches(term: any) {
  if (!term) return [];
  const current: any[] = [];
  const other: any[] = [];
  const allNotes = await gsVaultNotes();
  for (const { name: noteName, content } of allNotes) {
    const hits = findAllBareTerms(content, term);
    if (!hits.length) continue;
    const bucket = noteName === getCurrentFileName() ? current : other;
    for (const h of hits) {
      if (!matchInScope(h.head.length + h.index, content)) continue;
      // matchIndex is into the FULL content; the snippet is built from the masked body.
      bucket.push(
        buildMatch(noteName, h.body, h.index, h.length, h.matched, h.head.length + h.index)
      );
    }
  }
  return [...current, ...other];
}

// Inbound-link references across the vault (the backlinks / repoint matcher). Built
// into the standard result shape so rendering / selection / replace are unchanged.
// `name` is the searched note name; each hit is a literal link span. note-graph-index
// is reached via dynamic import so its graphology dep stays out of the eager bundle.
async function gsGetReferenceMatches(name: any) {
  if (!name) return [];
  const { findNoteReferences } = await import('./note-graph-index.js');
  const current: any[] = [];
  const other: any[] = [];
  const allNotes = await gsVaultNotes();
  for (const { name: noteName, content } of allNotes) {
    const hits = findNoteReferences(content, name);
    if (!hits.length) continue;
    const bucket = noteName === getCurrentFileName() ? current : other;
    for (const h of hits) {
      bucket.push(buildMatch(noteName, content, h.index, h.length, h.matched, h.index));
    }
  }
  return [...current, ...other];
}

export async function gsGetAllMatches(query: any, caseSensitive: any) {
  if (!query) return [];
  if (gsMode === 'bare') return gsGetBareMatches(query);
  if (isReferenceMode()) return gsGetReferenceMatches(query);
  const hasBoolOps = /\b(AND|OR|NOT)\b/i.test(query);

  const currentResults: any = [];
  const otherResults: any = [];

  const allNotes = await gsVaultNotes();

  const scanOccurrences = (
    haystack: string,
    needle: string,
    rawContent: string,
    noteName: string,
    bucket: any[]
  ) => {
    let pos = 0;
    while (true) {
      const idx = haystack.indexOf(needle, pos);
      if (idx === -1) break;
      pos = idx + needle.length;
      if (!matchInScope(idx, rawContent)) continue;
      bucket.push(
        buildMatch(
          noteName,
          rawContent,
          idx,
          needle.length,
          rawContent.slice(idx, idx + needle.length),
          idx
        )
      );
    }
  };

  if (!hasBoolOps) {
    const needle = caseSensitive ? query : query.toLowerCase();
    for (const { name: noteName, content: rawContent } of allNotes) {
      const haystack = caseSensitive ? rawContent : rawContent.toLowerCase();
      const bucket = noteName === getCurrentFileName() ? currentResults : otherResults;
      scanOccurrences(haystack, needle, rawContent, noteName, bucket);
    }
  } else {
    // Highlight occurrences only for POSITIVELY-required literals. The previous
    // `query.split(/\b(?:AND|OR|NOT)\b/i)` kept the NOT operand, so a query like
    // `foo OR NOT bar` rendered clickable hits for `bar` inside matching notes
    // even though `bar` was the EXCLUDED term. Split with the operators captured
    // so any literal that follows a NOT is dropped from the occurrence scan; the
    // match decision itself still honours NOT via createSearchPredicate below.
    const positiveTerms: string[] = [];
    let negateNext = false;
    for (const part of query.split(/\b(AND|OR|NOT)\b/i)) {
      const seg = part.trim();
      if (!seg) continue;
      if (/^(?:AND|OR|NOT)$/i.test(seg)) {
        negateNext = /^NOT$/i.test(seg);
        continue;
      }
      if (!negateNext) positiveTerms.push(seg);
      negateNext = false;
    }
    const makePred = (term: any) => (_n: any, c: any) => {
      return caseSensitive ? c.includes(term) : c.toLowerCase().includes(term.toLowerCase());
    };
    const predicate = createSearchPredicate(query, makePred);
    for (const { name: noteName, content: rawContent } of allNotes) {
      if (!predicate(noteName, rawContent)) continue;
      const bucket = noteName === getCurrentFileName() ? currentResults : otherResults;
      const haystack = caseSensitive ? rawContent : rawContent.toLowerCase();
      for (const term of positiveTerms) {
        const needle = caseSensitive ? term : term.toLowerCase();
        scanOccurrences(haystack, needle, rawContent, noteName, bucket);
      }
    }
  }
  return [...currentResults, ...otherResults];
}

/**
 * @param {string} text
 * @param {boolean | null} [ok]
 * @returns {void}
 */
function gsSetStatus(text: any, ok: any) {
  gsStatus.textContent = text;
  // The status lives inline at the find field's trailing edge ("Lens"), so it
  // ellipsises; mirror the full text into the title so a truncated transient
  // message stays readable on hover.
  if (text) gsStatus.title = text;
  else gsStatus.removeAttribute('title');
  gsStatus.style.color =
    ok === false ? 'var(--error)' : ok === true ? 'var(--success)' : 'var(--muted)';
}

// The inline match count at the find field's trailing edge. n=null clears it
// (empty query); otherwise shows the number with a "N matches in M notes" title.
function gsSetCount(n: number | null, results: any[] = []) {
  if (n == null) {
    gsStatus.textContent = '';
    gsStatus.removeAttribute('title');
    gsStatus.style.color = 'var(--muted)';
    return;
  }
  gsStatus.textContent = String(n);
  gsStatus.style.color = 'var(--muted)';
  const notes = new Set(results.map((r: any) => r.noteName)).size;
  gsStatus.title =
    n === 0
      ? 'No matches'
      : `${n} match${n === 1 ? '' : 'es'} in ${notes} note${notes === 1 ? '' : 's'}`;
}

// Retained-mode results list: reuse <li> nodes by key across renders (live-typing,
// post-replace refresh) instead of an innerHTML wipe. The list OWNS #gs-results.
let _gsKeyed: KeyedList | null = null;
function gsKeyed(): KeyedList {
  return (_gsKeyed ??= createKeyedList(gsResults));
}

function gsBuildSnippet(r: any): HTMLElement {
  const snippetSpan = document.createElement('span');
  snippetSpan.className = 'gs-snippet';
  snippetSpan.appendChild(document.createTextNode(r.prefix));
  const mark = document.createElement('mark');
  mark.textContent = r.match;
  snippetSpan.appendChild(mark);
  snippetSpan.appendChild(document.createTextNode(r.suffix));
  return snippetSpan;
}

// A match row is keyed positionally (`match_<i>`); the click handler closes over that
// index, and gsSelectResult reads gsCurrentResults[i] (rebuilt before every render),
// so a reused node at slot i always points at the current result.
function gsMountMatch(r: any, idx: number): HTMLElement {
  const li = document.createElement('li');
  li.className = 'gs-match';
  li.dataset.index = String(idx);
  li.appendChild(gsBuildSnippet(r));
  li.addEventListener('click', () => gsSelectResult(idx));
  return li;
}

function gsUpdateMatch(li: HTMLElement, r: any, idx: number): void {
  // Selection always resets to -1 on render, so a reused row must drop any stale
  // active highlight (the old innerHTML wipe cleared it for free).
  li.classList.remove('gs-active');
  li.dataset.index = String(idx);
  const old = li.querySelector('.gs-snippet');
  const next = gsBuildSnippet(r);
  if (old) li.replaceChild(next, old);
  else li.appendChild(next);
}

/**
 * @param {any[]} results
 * @param {string} [query]
 * @returns {void}
 */
function gsRenderResults(results: any, query: any) {
  gsSelectedIndex = -1;

  if (results.length === 0) {
    gsKeyed().render([]); // clears every row (the list owns #gs-results)
    gsSetCount(query ? 0 : null); // "0" at the field edge (null = empty query → blank)
    return;
  }

  // Inline match count at the find field's trailing edge (the "Lens" status).
  gsSetCount(results.length, results);

  // Results are note-consecutive (the current note's matches first, then each other
  // note's matches together), so a running note-name comparison groups them: a group
  // heading cell when the note changes, then each match as its own cell. Match cells
  // keep data-index → gsCurrentResults[index] so gsSelectResult + arrow-nav are
  // unaffected; group headings aren't selectable.
  const cells: Cell[] = [];
  let lastNote: any = null;
  results.forEach((r: any, i: number) => {
    if (r.noteName !== lastNote) {
      lastNote = r.noteName;
      const noteName = r.noteName;
      cells.push({
        key: `group_${noteName}`,
        mount: () => {
          const title = document.createElement('li');
          title.className = 'gs-group-title';
          title.textContent = noteName;
          return title;
        },
        update: (el) => {
          el.textContent = noteName;
        },
      });
    }
    const idx = i;
    const row = r;
    cells.push({
      key: `match_${idx}`,
      mount: () => gsMountMatch(row, idx),
      update: (el) => gsUpdateMatch(el, row, idx),
    });
  });

  gsKeyed().render(cells);
  // Match the old innerHTML='' behaviour: a fresh result set starts scrolled to top.
  gsResults.scrollTop = 0;
}

/**
 * @param {number} index
 * @returns {void}
 */
function gsSelectResult(index: any) {
  if (gsSelectedIndex >= 0) {
    const prev = gsResults.querySelector(`[data-index="${gsSelectedIndex}"]`);
    if (prev) prev.classList.remove('gs-active');
  }
  gsSelectedIndex = index;
  const item = gsResults.querySelector(`[data-index="${index}"]`);
  if (item) {
    item.classList.add('gs-active');
    item.scrollIntoView({ block: 'nearest' });
  }

  const result = gsCurrentResults[index];
  if (!result) return;

  // One unified jump: openLocation loads the note if needed (flushing the
  // current note's edits via loadNote), updates the address bar for
  // shareability + Back, and flashes the matched span identically to every
  // other jump source. We carry the precise char offset (exact, and correct
  // for boolean multi-term queries where the result's `match` is a single
  // term — not the whole query) plus `matchText` so a reopened link whose
  // offset has drifted re-anchors by text instead of landing wrong.
  openLocation({
    noteId: result.noteName,
    offset: result.matchIndex,
    length: result.match.length,
    matchText: result.match,
    caseSensitive: gsCaseCheckbox.checked,
    source: 'search',
  });
}

/** @returns {Promise<void>} */
async function gsRunFind() {
  const query = gsSearchInput.value;
  if (!query) {
    gsSetStatus('Enter a search term.', false);
    return;
  }
  gsCurrentResults = await gsGetAllMatches(query, gsCaseCheckbox.checked);
  gsRenderResults(gsCurrentResults, query);
}

// ── Event listeners ───────────────────────────────────────────────────────

gsCloseBtn.addEventListener('click', closeGlobalSearch);

// Scope chip (#6): toggles All notes ⇄ This note (scoping to the current file is
// sticky, so typing keeps it). Either choice re-runs the active search.
function setScope(toNote: boolean) {
  if (toNote) {
    gsScopeNote = getCurrentFileName();
    gsScopeSticky = !!gsScopeNote;
  } else {
    gsScopeNote = null;
    gsScopeSticky = false;
  }
  applyScopeLabel();
  if (gsSearchInput.value) gsRunFind();
}
gsScopeToggle?.addEventListener('click', () => setScope(!(gsScopeSticky && gsScopeNote)));

// Title-scope chip: cycles whole-note → content → title and re-runs.
gsTitleToggle?.addEventListener('click', () => {
  gsTitleScope = TITLE_STATES[gsTitleScope].next;
  applyTitleScope();
  if (gsSearchInput.value) gsRunFind();
});

// Replace chip: discloses / collapses the replace block; focuses the field on show.
gsReplaceToggle?.addEventListener('click', () => {
  const show = gsReplaceBlock?.hidden !== false; // currently hidden → reveal
  discloseReplace(show);
  if (show) gsReplaceInput?.focus();
});

gsSearchInput.addEventListener('keydown', (e: any) => {
  if (e.key === 'Enter') gsRunFind();
});

// Debounced live search. A manual edit drops any one-shot single-note alias scope (→ vault-
// wide) and returns backlinks/manual modes to the normal substring search. Bare mode is the
// deliberate exception: it STAYS bare, because the alias replacement still prefilled in the
// Replace box (e.g. [dad](…)) applied by a plain substring Replace-all would re-wrap existing
// links into [[dad](…)](…) and hit mid-word matches (Baghdad) vault-wide. Vault-wide bare is
// the original link-safe, whole-word alias-propagation flow, so staying bare keeps it safe.
gsSearchInput.addEventListener('input', () => {
  // A STICKY scope (Cmd-F "find in this note", #6) survives manual typing; the
  // one-shot alias-consolidation scope (never sticky) drops on the first keystroke.
  gsScopeNote = gsScopeSticky ? gsScopeNote : null;
  if (gsMode !== 'bare') gsMode = 'manual';
});
gsSearchInput.addEventListener(
  'input',
  debounce(() => {
    if (gsSearchInput.value) {
      gsRunFind();
    } else {
      gsKeyed().render([]); // the keyed list owns #gs-results — don't wipe it raw
      gsStatus.textContent = '';
      gsCurrentResults = [];
      gsSelectedIndex = -1;
    }
  }, 300)
);

gsCaseCheckbox.addEventListener('change', () => {
  applyCaseLabel();
  if (gsSearchInput.value) gsRunFind();
});
// Sync the toggle labels with their initial state on load.
applyCaseLabel();
applyTitleScope();
applyScopeLabel();

// Replace in the currently selected note (first occurrence)
/** Persist one note's replaced content: write it, mirror into the open editor if
 *  it's the current note, then follow any title-line rename. */
async function persistReplacement(noteName: string, newContent: string): Promise<void> {
  await window.NoteStorage.setNote(noteName, newContent);
  if (getCurrentFileName() === noteName) {
    NoteEditor.replaceAll(newContent);
    reflectActiveView();
  }
  await handleRenameAfterReplace(noteName, newContent);
  // Record the write so the conflict sweep never forks it as a spurious "(conflicted copy)".
  // The crypto wrapper already records this when encryption is on; recording here too covers a
  // no-encryption setup (where window.NoteStorage isn't the crypto layer) and is harmless
  // otherwise (same name+content). See storage/note-local-echo.
  recordLocalEcho(noteName, newContent);
  // Immediate, note-named signal for live views (the Labyrinth thread alias card re-checks this
  // note). powersync:change also fires on a content write, but it's debounced ~100ms, fires
  // ~twice (the local write + its sync echo) and carries no note name — so a direct event is
  // the better trigger here.
  window.dispatchEvent(new CustomEvent('notes:replaced', { detail: { note: noteName } }));
}

/** After replacing, refresh the file list and re-run the active search so the
 *  result list reflects the new content. */
async function refreshAfterReplace(query: any, caseSensitive: any): Promise<void> {
  await updateFileList();
  gsCurrentResults = await gsGetAllMatches(query, caseSensitive);
  gsRenderResults(gsCurrentResults, query);
}

gsReplaceBtn.addEventListener('click', async () => {
  if (gsSelectedIndex < 0) {
    gsSetStatus('Select a result first.', false);
    return;
  }
  const query = gsSearchInput.value;
  const replacement = gsReplaceInput.value;
  const caseSensitive = gsCaseCheckbox.checked;
  if (!query) return;

  // Bare/alias mode: replace ONLY the selected bare occurrence (carrying its
  // casing), so a different "Matt" elsewhere in the note is never touched.
  if (gsMode === 'bare') {
    const result = gsCurrentResults[gsSelectedIndex];
    const content = await window.NoteStorage.getNote(result.noteName);
    if (content === null) {
      gsSetStatus('Note not found.', false);
      return;
    }
    const hits = findAllBareTerms(content, query);
    const hit = hits.find((h) => h.head.length + h.index === result.matchIndex) ?? hits[0];
    if (!hit) {
      gsSetStatus('Match no longer found (content changed).', false);
      return;
    }
    const newContent = replaceBareHits(content, query, replacement, [hit]);
    await persistReplacement(result.noteName, newContent);
    await refreshAfterReplace(query, caseSensitive);
    gsSetStatus(`Replaced 1 match in “${result.noteName}”.`, true);
    return;
  }

  // Reference mode: repoint every inbound link to `query` within the selected note
  // onto `replacement` (repointLinks is note-granular). A blank Replace is read-only.
  if (isReferenceMode()) {
    if (!replacement) {
      gsSetStatus('Enter a target note to repoint to.', false);
      return;
    }
    const result = gsCurrentResults[gsSelectedIndex];
    const content = await window.NoteStorage.getNote(result.noteName);
    if (content === null) {
      gsSetStatus('Note not found.', false);
      return;
    }
    const { findNoteReferences, repointLinks } = await import('./note-graph-index.js');
    if (!findNoteReferences(content, query).length) {
      gsSetStatus('No references found (content changed).', false);
      return;
    }
    await persistReplacement(result.noteName, repointLinks(content, query, replacement));
    await refreshAfterReplace(query, caseSensitive);
    gsSetStatus(`Repointed references in “${result.noteName}”.`, true);
    return;
  }

  const result = gsCurrentResults[gsSelectedIndex];
  let content = await window.NoteStorage.getNote(result.noteName);
  if (content === null) {
    gsSetStatus(`Note not found.`, false);
    return;
  }

  // Replace the SELECTED occurrence (result.matchIndex) — not merely the first
  // one in the note, which may sit in the title (outside an active title scope)
  // or be a different occurrence than the row the user picked.
  const idx = result.matchIndex;
  const seg = content.slice(idx, idx + result.match.length);
  if (seg !== result.match) {
    gsSetStatus('Match no longer found (content changed).', false);
    return;
  }

  content = content.slice(0, idx) + replacement + content.slice(idx + result.match.length);
  await persistReplacement(result.noteName, content);
  await refreshAfterReplace(query, caseSensitive);
  gsSetStatus(`Replaced 1 match in \u201c${result.noteName}\u201d.`, true);
});

// Replace all occurrences across all notes
gsReplaceAllBtn.addEventListener('click', async () => {
  const query = gsSearchInput.value;
  const replacement = gsReplaceInput.value;
  const caseSensitive = gsCaseCheckbox.checked;
  if (!query) {
    gsSetStatus('Enter a search term.', false);
    return;
  }

  // Reference mode: repoint every inbound link to `query` onto `replacement`,
  // reviewed across the vault (the same rewrite applyMerge does, surfaced for review).
  if (isReferenceMode()) {
    if (!replacement) {
      gsSetStatus('Enter a target note to repoint to.', false);
      return;
    }
    const fresh = await gsGetReferenceMatches(query);
    if (fresh.length === 0) {
      gsSetStatus('No references to repoint.', false);
      return;
    }
    const affected = [...new Set(fresh.map((r) => r.noteName))];
    const ok = await confirmStatus(
      `Repoint all ${fresh.length} reference${fresh.length === 1 ? '' : 's'} ` +
        `to “${replacement}” across ${affected.length} note${affected.length === 1 ? '' : 's'}?`,
      { confirmLabel: 'Repoint all' }
    );
    if (!ok) return;
    const { repointLinks } = await import('./note-graph-index.js');
    const _one = async (noteName: any) => {
      const content = await window.NoteStorage.getNote(noteName);
      if (content === null) return;
      const after = repointLinks(content, query, replacement);
      if (after !== content) await persistReplacement(noteName, after);
    };
    await mapChunked(affected, _one);
    await refreshAfterReplace(query, caseSensitive);
    gsSetStatus(
      `Repointed references across ${affected.length} note${affected.length === 1 ? '' : 's'}.`,
      true
    );
    return;
  }

  // Bare/alias mode: wrap every whole-word bare occurrence across the vault,
  // carrying casing, never touching titles/code/existing links.
  if (gsMode === 'bare') {
    const fresh = await gsGetBareMatches(query);
    if (fresh.length === 0) {
      gsSetStatus('No matches to replace.', false);
      return;
    }
    const affected = [...new Set(fresh.map((r) => r.noteName))];
    const ok = await confirmStatus(
      `Replace all ${fresh.length} match${fresh.length === 1 ? '' : 'es'} ` +
        `across ${affected.length} note${affected.length === 1 ? '' : 's'}?`,
      { confirmLabel: 'Replace all' }
    );
    if (!ok) return;
    let totalReplaced = 0;
    const _bareOne = async (noteName: any) => {
      const content = await window.NoteStorage.getNote(noteName);
      if (content === null) return;
      const hits = findAllBareTerms(content, query);
      if (!hits.length) return;
      totalReplaced += hits.length;
      await persistReplacement(noteName, replaceBareHits(content, query, replacement, hits));
    };
    await mapChunked(affected, _bareOne);
    await refreshAfterReplace(query, caseSensitive);
    gsSetStatus(
      `Replaced ${totalReplaced} match${totalReplaced === 1 ? '' : 'es'} ` +
        `across ${affected.length} note${affected.length === 1 ? '' : 's'}.`,
      true
    );
    return;
  }

  const fresh = await gsGetAllMatches(query, caseSensitive);
  if (fresh.length === 0) {
    gsSetStatus('No matches to replace.', false);
    return;
  }

  const affected = [...new Set(fresh.map((r) => r.noteName))];
  const totalCount = fresh.length;

  const ok = await confirmStatus(
    `Replace all ${totalCount} match${totalCount === 1 ? '' : 'es'} ` +
      `across ${affected.length} note${affected.length === 1 ? '' : 's'}?`,
    { confirmLabel: 'Replace all' }
  );
  if (!ok) return;

  const escaped = escapeRegExp(query);
  const flags = caseSensitive ? 'g' : 'gi';
  const regex = new RegExp(escaped, flags);
  let totalReplaced = 0;

  const _replaceOne = async (noteName: any) => {
    const content = await window.NoteStorage.getNote(noteName);
    if (content === null) return;
    // Honour the title-scope chip so Replace All only touches the region the
    // (scoped) Find surfaced. Without this, a 'Content only' / 'Title only'
    // scope silently rewrites the excluded region (incl. renaming notes via
    // their '# Title' line). For 'both' the slice is the whole doc → unchanged.
    const te = titleEnd(content);
    const lo = gsTitleScope === 'content' ? te : 0;
    const hi = gsTitleScope === 'title' ? te : content.length;
    const region = content.slice(lo, hi);
    const matches = region.match(regex);
    if (!matches) return;
    totalReplaced += matches.length;
    await persistReplacement(
      noteName,
      content.slice(0, lo) + region.replace(regex, () => replacement) + content.slice(hi)
    );
  };
  await mapChunked(affected, _replaceOne);

  await refreshAfterReplace(query, caseSensitive);
  gsSetStatus(
    `Replaced ${totalReplaced} match${totalReplaced === 1 ? '' : 'es'} ` +
      `across ${affected.length} note${affected.length === 1 ? '' : 's'}.`,
    true
  );
});

// Arrow-key navigation through results; Escape closes the panel.
globalSearchPanel.addEventListener('keydown', (e: any) => {
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    const next = Math.min(gsSelectedIndex + 1, gsCurrentResults.length - 1);
    if (next >= 0) gsSelectResult(next);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    const prev = Math.max(gsSelectedIndex - 1, 0);
    if (gsCurrentResults.length > 0) gsSelectResult(prev);
  } else if (e.key === 'Escape') {
    e.preventDefault();
    closeGlobalSearch();
  }
});

// The window.openGlobalSearch / window.closeGlobalSearch back-compat shims had
// no readers left (app-init imports the ESM names) and are gone.

/** Open the panel as a "find references" to a note (defaults to the current note):
 *  every inbound [[name]] / [disp](name) link. Replace stays optional — type a target
 *  note + "Repoint all" to repoint those references onto it. */
function openBacklinks(noteName?: string) {
  const name = noteName ?? getCurrentFileName();
  if (!name) return;
  openGlobalSearch({ mode: 'backlinks', find: name, replace: '', run: true });
}

/** Open Find & Replace scoped to the CURRENT note (Cmd-F, #6). The scope is
 *  STICKY (survives typing) and toggleable off via the scope chip. Falls back to
 *  an unscoped all-notes search when there's no current note (Labyrinth/settings). */
function openNoteSearch() {
  const n = getCurrentFileName();
  openGlobalSearch(n ? { scopeNote: n, persistScope: true } : {});
}

export { closeGlobalSearch, gsRenderResults, openBacklinks, openGlobalSearch, openNoteSearch };
